import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FileText, X } from "lucide-react"
import { type ChangeEvent } from "react"

interface FileListUploadProps {
  id: string
  label: string
  helpText?: string
  accept?: string
  maxFiles?: number
  files: File[]
  onFilesChange: React.Dispatch<React.SetStateAction<File[]>>
}

export function FileListUpload({
  id,
  label,
  helpText,
  accept,
  maxFiles = 3,
  files,
  onFilesChange,
}: FileListUploadProps) {
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? [])

    if (selectedFiles.length === 0) {
      return
    }

    onFilesChange((currentFiles) =>
      [...currentFiles, ...selectedFiles].slice(0, maxFiles),
    )

    event.target.value = ""
  }

  const handleRemoveFile = (indexToRemove: number) => {
    onFilesChange((currentFiles) =>
      currentFiles.filter((_, index) => index !== indexToRemove),
    )
  }

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="file"
        accept={accept}
        multiple
        onChange={handleFileChange}
      />
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between rounded-md border p-2"
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm">{file.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => handleRemoveFile(index)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
